import { showMessageError, addTimezoneToMoment } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import {
  addSineWaveToEcgData,
  downloadSelectedEcgData,
  filterHourlySummaryRanges,
} from '@/entities/study';
import type { InboxECGViewerProps } from './inboxEcgViewer.types';

/**
 * Drives the inbox ECG viewer for a single event, loading its strip and exposing the back action. Keeps title info in a ref and the chart data in merge state, reloads through createData whenever the event data changes, and returns that state, the title ref and an onClickBack that clears the title before calling the parent callback.
 *
 * @param data - The inbox event data carrying study, patient and clinic ECG metadata.
 * @param handleClickBack - Parent callback invoked when the viewer's back action is used.
 * @returns The viewer state, the title info ref and the back handler.
 */
const useInboxEcgViewer = ({ data, handleClickBack }: InboxECGViewerProps) => {
  const titleInfo = useRef<any>({});

  const [state, setState] = useMergeState<any>({
    ecgData: undefined,
    ecgObject: {},
    configChart: {},
    isLoadedData: false,
  });

  /**
   * Deinterleaves a flat binary ECG buffer into one sample array per channel. Walks the buffer in strides of totalChannel appending each value to its channel array, and yields undefined when no binary data is supplied.
   *
   * @param totalChannel - The number of interleaved channels.
   * @param binaryData - The flat interleaved ECG sample buffer.
   * @returns The per-channel sample arrays, or undefined without data.
   */
  const convertBinaryToECGData = (totalChannel: number, binaryData: any) => {
    if (binaryData) {
      const ecgData = new Array(totalChannel);
      for (let i = 0; i < binaryData.length; i += totalChannel) {
        for (let j = 0; j < totalChannel; j += 1) {
          if (ecgData[j]) {
            ecgData[j].push(binaryData[i + j]);
          } else {
            ecgData[j] = [binaryData[i + j]];
          }
        }
      }
      return ecgData;
    }
    return undefined;
  };

  /**
   * Loads and assembles the ECG strip for the selected inbox event. Computes the event sample position from the event time and study start, builds timezone-adjusted start and stop moments, filters the hourly summary ranges, downloads and deinterleaves the ECG data, pads it with sine waves, then stores the chart object, config, time object and title info in state, toasting on failure.
   *
   * @returns A promise resolving once the event data is in state.
   */
  const createData = async () => {
    try {
      setState({ isLoadedData: false });

      const {
        studyFid,
        patientName,
        interprettingPhysicianName,
        startStudyDate,
        eventTime,
        timezone,
        facilityCountry,
        facilityTimezone,
        clinicData,
        studyTimeOffset,
      } = data;
      const { start, stop, ecgDataMap } = clinicData;
      const { samplingFrequency, gain, channels, querySignature } = ecgDataMap;

      const eventDiffSeconds = Math.abs(dayjs(eventTime).valueOf() - dayjs(start).valueOf()) / 1000;
      const eventPos = Math.round(eventDiffSeconds * samplingFrequency);

      const startMoment = addTimezoneToMoment({
        startStudyDate,
        dateMoment: dayjs(start),
        timezone,
        facilityCountry,
        facilityTimezone,
      });
      const stopMoment = addTimezoneToMoment({
        startStudyDate,
        dateMoment: dayjs(stop),
        timezone,
        facilityCountry,
        facilityTimezone,
      });
      const ranges = filterHourlySummaryRanges({
        summaries: ecgDataMap,
        start: startMoment,
        stop: stopMoment,
        channels,
        samplingFrequency,
        willAddDataRanges: true,
      });
      const downloadedSelectedEcgData = await downloadSelectedEcgData(ranges, querySignature);
      const selectedEcgData = convertBinaryToECGData(channels.length, downloadedSelectedEcgData);
      const { ecgData: addedSineWaveSelectedEcgData } = addSineWaveToEcgData(
        selectedEcgData,
        ranges,
        startMoment,
        stopMoment,
        ecgDataMap,
      );

      let ecgObject = {};
      if (addedSineWaveSelectedEcgData && addedSineWaveSelectedEcgData[0].length) {
        ecgObject = {
          adcGain: gain,
          adcZero: 0,
          numChannels: channels.length,
          samplingRate: samplingFrequency,
          dataSamples: addedSineWaveSelectedEcgData,
          eventPos,
          isShowBeatMarkers: true,
        };
      }

      titleInfo.current = {
        studyFid,
        patientName,
        interprettingPhysicianName,
        startStudyDate,
        eventTime,
        timezone,
        studyTimeOffset,
        facilityCountry,
        facilityTimezone,
      };

      setState({
        ecgData: addedSineWaveSelectedEcgData,
        ecgObject,
        configChart: {
          isJumpOffset: true,
          isDisplayScrollbar: true,
          isEdit: true,
        },
        isLoadedData: true,
        timeObject: {
          startMoment: dayjs(data?.clinicData?.start).valueOf(),
          timezone: data?.timezone,
        },
      });
    } catch (error) {
      console.error('Failed to fetch event: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    }
  };

  /**
   * Leaves the inbox ECG viewer and returns to the list. Clears the cached title info ref then invokes the parent handleClickBack callback.
   *
   * @returns Nothing.
   */
  const onClickBack = () => {
    titleInfo.current = {};
    handleClickBack();
  };

  useEffect(() => {
    createData();
  }, [data]);

  return {
    state,
    titleInfo,
    onClickBack,
  };
};

export { useInboxEcgViewer };
